import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useApiClients } from './ApiProvider';
import { useTheme, type ThemeMode } from './ThemeProvider';
import { clearStoredToken } from './auth';
import { orgRoute } from './org-route';

/**
 * Top-right user-identity disclosure. Consolidates everything the
 * operator wants to do "about themselves":
 *
 *   - identity card (avatar + display name + role + email)
 *   - theme switcher (Light / System / Dark) with the same three-state
 *     semantic the old standalone ThemeToggle had
 *   - link to /settings (S11)
 *   - sign out (only when bearer-token auth is active; dev tokens
 *     don't have a sign-out endpoint, so the action falls back to
 *     clearing the local override)
 *
 * Replaces the old standalone ThemeToggle pill so the right-side
 * cluster stops being a row of unlabeled icons + a "what does this
 * button do?" Search affordance. Native <details> for menu behavior
 * (keyboard accessible by default), augmented with outside-click +
 * ESC handlers because the native element doesn't close on its own
 * when focus leaves it.
 */
export function UserMenu() {
  const clients = useApiClients();
  const { mode, setMode } = useTheme();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Outside-click + ESC handlers — native <details> stays open until
  // the summary is clicked again, which is a real UX bug (every
  // popover library closes on outside click; we matched that on PR
  // review feedback).
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const el = detailsRef.current;
      if (!el || !el.open) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        el.open = false;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      const el = detailsRef.current;
      if (!el || !el.open) return;
      if (e.key === 'Escape') {
        el.open = false;
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  const me = useQuery({
    queryKey: ['users', 'me'],
    queryFn: async () => {
      const { data, error } = await clients.apiGateway.GET('/v1/users/me');
      if (error || !data) return null;
      return data;
    },
    staleTime: 5 * 60_000,
  });

  const displayName = me.data?.display_name ?? me.data?.email ?? 'You';
  const email = me.data?.email ?? '';
  const role = me.data?.normalized_role ?? me.data?.role ?? '';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <details ref={detailsRef} className="relative group">
      <summary
        aria-label="User menu"
        className="list-none inline-flex items-center gap-sp-2 cursor-pointer rounded-full border border-gray-300 bg-gray-50 py-sp-1 pl-sp-1 pr-sp-2 text-fs-meta text-gray-900 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info group-open:border-gray-500 group-open:bg-gray-100"
      >
        <span
          aria-hidden
          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 font-medium text-gray-50"
        >
          {initial}
        </span>
        <span
          aria-hidden
          className="inline-block transition-transform group-open:rotate-180"
        >
          ▾
        </span>
      </summary>
      <div
        role="menu"
        className="absolute right-0 z-30 mt-sp-2 flex w-72 flex-col gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-4 shadow-lg"
      >
        <IdentityCard
          initial={initial}
          displayName={displayName}
          email={email}
          role={role}
        />

        <hr className="border-gray-300" />

        <ThemeSection mode={mode} setMode={setMode} />

        <hr className="border-gray-300" />

        <ul className="flex flex-col gap-sp-1">
          <li role="none">
            <Link
              to={orgRoute('/settings')}
              role="menuitem"
              className="block rounded-r-default px-sp-2 py-sp-2 text-fs-body text-gray-900 hover:bg-gray-100"
            >
              Settings →
            </Link>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                // Sign-out drops the bearer token + clears the query
                // cache so the next render doesn't reuse the previous
                // user's /me + capabilities. Subsequent API calls go
                // out with no Authorization header and the api-gateway
                // RequireBearer middleware responds with 401.
                clearStoredToken();
                queryClient.clear();
                if (detailsRef.current) detailsRef.current.open = false;
                navigate({ to: '/auth' });
              }}
              className="block w-full rounded-r-default px-sp-2 py-sp-2 text-left text-fs-body text-gray-900 hover:bg-gray-100"
            >
              Sign out
            </button>
          </li>
        </ul>
      </div>
    </details>
  );
}

function IdentityCard({
  initial,
  displayName,
  email,
  role,
}: {
  initial: string;
  displayName: string;
  email: string;
  role: string;
}) {
  return (
    <div className="flex items-center gap-sp-3">
      <span
        aria-hidden
        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-fs-statement font-medium text-gray-50"
      >
        {initial}
      </span>
      <div className="min-w-0">
        <p className="truncate text-fs-body font-medium text-gray-900">
          {displayName}
        </p>
        {email && email !== displayName ? (
          <p className="truncate font-mono text-fs-meta text-gray-700">{email}</p>
        ) : null}
        {role ? (
          <p className="mt-sp-1 inline-flex items-center gap-sp-1 rounded-r-chip bg-info-bg px-sp-2 py-sp-1 text-fs-meta text-info">
            {role}
          </p>
        ) : null}
      </div>
    </div>
  );
}

const THEME_OPTIONS: { mode: ThemeMode; label: string; glyph: string; help: string }[] = [
  { mode: 'light', label: 'Light', glyph: '☀', help: 'Always light' },
  { mode: 'system', label: 'System', glyph: '◐', help: 'Follow OS' },
  { mode: 'dark', label: 'Dark', glyph: '☾', help: 'Always dark' },
];

function ThemeSection({
  mode,
  setMode,
}: {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
}) {
  return (
    <div className="flex flex-col gap-sp-2">
      <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        Theme
      </p>
      <div
        role="radiogroup"
        aria-label="Color theme"
        className="grid grid-cols-3 gap-sp-1 rounded-r-default border border-gray-300 bg-gray-50 p-sp-1"
      >
        {THEME_OPTIONS.map((opt) => {
          const active = mode === opt.mode;
          return (
            <button
              key={opt.mode}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`${opt.label} theme — ${opt.help}`}
              title={opt.help}
              onClick={() => setMode(opt.mode)}
              className={
                'inline-flex items-center justify-center gap-sp-1 rounded-r-chip px-sp-2 py-sp-1 text-fs-meta font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
                (active
                  ? 'bg-gray-900 text-gray-50'
                  : 'text-gray-700 hover:bg-gray-100')
              }
            >
              <span aria-hidden>{opt.glyph}</span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
